import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import axios, { API } from "@/lib/api";
import { Loader2, Mail, Lock, Sparkles } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import { formatApiErrorDetail } from "@/lib/authForms";

export default function SignIn() {
  const navigate = useNavigate();
  const { user, loading, refresh } = useAuth();
  const [tab, setTab] = useState("email"); // "email" | "google"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && user) navigate("/app", { replace: true });
  }, [user, loading, navigate]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/login`, { email: email.trim().toLowerCase(), password });
      await refresh();
      navigate("/app", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, "Sign-in failed."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Sign in"
      subtitle="Welcome back. Pick the method you signed up with — both lead to the same studio."
      footer={
        <span>
          Don't have an account?{" "}
          <Link to="/signup" data-testid="signin-go-signup" className="text-[#D4AF37] hover:text-[#F3CE5A] transition">
            Create one →
          </Link>
        </span>
      }
    >
      {/* Tabs */}
      <div className="mt-7 inline-flex w-full rounded-full border border-white/10 p-0.5 text-xs bg-[#0F0F0F]">
        <button
          type="button"
          onClick={() => setTab("email")}
          data-testid="signin-tab-email"
          className={`flex-1 px-4 py-2 rounded-full transition inline-flex items-center justify-center gap-1.5 ${
            tab === "email" ? "bg-[#D4AF37] text-black" : "text-white/65 hover:text-white"
          }`}
        >
          <Mail className="w-3.5 h-3.5" /> Email
        </button>
        <button
          type="button"
          onClick={() => setTab("google")}
          data-testid="signin-tab-google"
          className={`flex-1 px-4 py-2 rounded-full transition inline-flex items-center justify-center gap-1.5 ${
            tab === "google" ? "bg-[#D4AF37] text-black" : "text-white/65 hover:text-white"
          }`}
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="currentColor" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.3 6.2 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
          </svg>
          Google
        </button>
      </div>

      {loading ? (
        <div className="mt-8 flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />
        </div>
      ) : tab === "email" ? (
        <form onSubmit={handleEmailLogin} className="mt-6 space-y-4" data-testid="signin-email-form" id="facetra-customer-signin" autoComplete="on">
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5" htmlFor="facetra-signin-email">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
              <input
                id="facetra-signin-email"
                name="facetra-signin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                data-testid="signin-email-input"
                className="w-full bg-[#141414] border border-white/10 rounded-xl pl-9 pr-3 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
                placeholder="you@studio.com"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] uppercase tracking-[0.22em] text-white/45" htmlFor="facetra-signin-pw">Password</label>
              <Link to="/forgot-password" data-testid="signin-forgot" className="text-[10px] text-[#D4AF37] hover:text-[#F3CE5A]">
                Forgot?
              </Link>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
              <input
                id="facetra-signin-pw"
                name="facetra-signin-pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="signin-password-input"
                className="w-full bg-[#141414] border border-white/10 rounded-xl pl-9 pr-3 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
                placeholder="••••••••"
              />
            </div>
          </div>
          {error && (
            <p data-testid="signin-error" className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting || !email || !password}
            data-testid="signin-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3.5 hover:bg-[#F3CE5A] transition disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_30px_-8px_rgba(212,175,55,0.55)]"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : <>Sign in</>}
          </button>
        </form>
      ) : (
        <GoogleAuthButton next="/app" label="Continue with Google" />
      )}

      <div className="my-7 flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-white/30">
        <div className="flex-1 h-px bg-white/10" /> or <div className="flex-1 h-px bg-white/10" />
      </div>

      <Link
        to="/signup"
        data-testid="signin-go-signup"
        className="block text-center text-sm text-[#D4AF37] hover:text-[#F3CE5A] transition"
      >
        <Sparkles className="w-3.5 h-3.5 inline -mt-0.5 mr-1.5" />
        Don&apos;t have an account? Create one →
      </Link>
    </AuthShell>
  );
}
